import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import Slider from "react-slick";

import FloorplanCustomiser from "../components/FloorplanCustomiser";
import FloorplanLightbox from "../components/FloorplanLightbox";
import GoogleMap from "../components/GoogleMap";
import Icon from "../components/Icon";
import MoreDetailsBtn from "../components/MoreDetailsBtn";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import { FloorplanOption, IconName } from "../types";
import { scrollToTop } from "../util";

const slickSettings = {
  infinite: true,
  arrows: true,
  dots: false,
  centerMode: true,
  variableWidth: true,
  touchThreshold: 500,
  className: "gallery-slider",
  responsive: [
    {
      breakpoint: 991,
      settings: {
        variableWidth: false,
        centerMode: false,
        arrows: false,
      },
    },
  ],
};

const HouseLandDetailsScreen = observer(() => {
  const userContext = useContext(UserContext);
  const apiContext = useContext(ApiContext);
  const navContext = useContext(NavigationContext);

  const [lightboxSvg, setLightboxSvg] = useState("");
  const [isLightboxShown, setIsLightboxShown] = useState(false);

  useEffect(() => {
    scrollToTop();
    navContext.setFooterModePrimary();
    navContext.setNextBtnText("Select Package");
    navContext.setNextButtonEnabled(true);
    navContext.setOnNavigateBack(() => {
      userContext.resetSelectedFloorplanOptions();
      userContext.resetIsFloorplanFlipped();
    });

    // If the user entered the app via a House link on the main website,
    // we need to pre-select the floorplan options for the home design
    if (
      apiContext.enteredViaHome &&
      userContext.selectedHouseLand &&
      apiContext.enteredViaHome.id === userContext.selectedHouseLand.home_id &&
      !userContext.selectedFloorplanOptions.get(
        userContext.selectedHouseLand.id
      ) &&
      userContext.selectedFloorplanOptions.get(apiContext.enteredViaHome.id)
    ) {
      userContext.setSelectedFloorplanOptions(
        userContext.selectedHouseLand.id,
        userContext.selectedFloorplanOptions.get(
          apiContext.enteredViaHome.id
        ) || []
      );
    }
    return () => {
      // Unbind the handler when we unmount
      navContext.resetOnNavigateBack();
    };
  }, []);

  const onClickBack = () => {
    navContext.goBack();
  };

  const onClickPdf = () => {
    if (userContext.selectedHouseLand) {
      const url = `/house-and-land-packages/${userContext.selectedHouseLand.slug}/pdf`;
      window.open(url, "_blank");
    }
  };

  const onClickGetDirections = () => {
    if (userContext.selectedHouseLand) {
      const h = userContext.selectedHouseLand;
      const address = `${h.address} ${h.suburb} ${h.state} ${h.postcode} Australia`;
      const url =
        "https://www.google.com/maps/search/?api=1&query=" + encodeURI(address);
      window.open(url, "_blank");
    }
  };

  const onClickShowLightbox = (svg: string) => {
    // The user can click the lightbox link on the next item in the carousel on desktop
    setLightboxSvg(svg);
    setIsLightboxShown(true);
  };

  const onClickFloorplanOption = (option: FloorplanOption) => {
    if (userContext.selectedHouseLand) {
      userContext.toggleSelectedFloorplanOption(
        userContext.selectedHouseLand.id,
        option
      );
    }
  };

  const onFlipFloorplan = (isFlipped: boolean) => {
    if (userContext.selectedHouseLand) {
      userContext.setIsFloorplanFlipped(
        userContext.selectedHouseLand.id,
        isFlipped
      );
    }
  };

  return (
    <div className={"screen screen-house-land-details"}>
      {userContext.selectedHouseLand && (
        <>
          <div className={"container"}>
            <div
              className={"back-bar"}
              role={"button"}
              onClick={() => onClickBack()}
            >
              <Icon name={IconName.CHEVRON_DOWN} />
              Back to Listings
            </div>
            <h2 className="h1">
              {userContext.selectedHouseLand.address}{" "}
              {userContext.selectedHouseLand.suburb}{" "}
              {userContext.selectedHouseLand.state}{" "}
              {userContext.selectedHouseLand.postcode}
            </h2>
          </div>
          <div className={"slider module-gallery"}>
            <Slider {...slickSettings}>
              {userContext.selectedHouseLand.gallery_images.map((image) => {
                return (
                  <div className={"slide"} key={image}>
                    <div className="slide-inner">
                      <div className="image">
                        <div className="bg-image">
                          <img src={image} alt={""} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
          <div className={"pricing mobile container"}>
            <p className={"section-label"}>
              {userContext.selectedHouseLand.pdf_fixed_price
                ? "Fixed Price"
                : " "}
            </p>
            <p className={"price"}>
              ${userContext.selectedHouseLand.price.toLocaleString()}*
            </p>
          </div>
          <div className={"details-container container"}>
            <div className={"icons-row"}>
              <div className={"icons-item"}>
                <Icon name={IconName.BED} />
                <span>{userContext.selectedHouseLand.bedrooms}</span>
              </div>
              <div className={"icons-item"}>
                <Icon name={IconName.COUCH} />
                <span>{userContext.selectedHouseLand.living_areas}</span>
              </div>
              <div className={"icons-item"}>
                <Icon name={IconName.BATH} />
                <span>{userContext.selectedHouseLand.bathrooms}</span>
              </div>
              <div className={"icons-item"}>
                <Icon name={IconName.CAR} />
                <span>{userContext.selectedHouseLand.car_spaces}</span>
              </div>
            </div>

            <div className={"pdf-row"}>
              <MoreDetailsBtn
                onClick={onClickPdf}
                title={"Download PDF Flyer"}
              />
            </div>

            <div className={"details-cols"}>
              <div className={"details"}>
                <div className={"details-row"}>
                  <span className={"label"}>House Design</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.house_design}
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>Facade</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.facade_title}
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>Facade Finish</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.facade_finish}
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>Exterior Colour Scheme</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.exterior_colour}
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>Lot Size</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.lot_size}m&sup2;
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>House Size</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.total_area_m}m&sup2; (
                    {userContext.selectedHouseLand.total_area_sq}
                    sq)
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>House Width</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.width}m
                  </span>
                </div>
                <div className={"details-row"}>
                  <span className={"label"}>House Length</span>
                  <span className={"separator"}>:</span>
                  <span className={"value"}>
                    {userContext.selectedHouseLand.length}m
                  </span>
                </div>
              </div>
              <div className={"pricing desktop"}>
                <p className={"section-label"}>
                  {userContext.selectedHouseLand.pdf_fixed_price
                    ? "Fixed Price"
                    : " "}
                </p>
                <p className={"price"}>
                  ${userContext.selectedHouseLand.price.toLocaleString()}*
                </p>

                <MoreDetailsBtn
                  onClick={onClickPdf}
                  title={"Download PDF Flyer"}
                />
              </div>
            </div>
          </div>

          <div className={"container floorplan-container"}>
            <FloorplanCustomiser
              isHouseLand
              home={userContext.selectedHouseLand}
              isActive={true}
              isFloorplanFlipped={
                userContext.isFloorplanFlipped.get(
                  userContext.selectedHouseLand.id
                ) || false
              }
              onFlipFloorplan={(isFlipped) => onFlipFloorplan(isFlipped)}
              selectedOptions={
                userContext.selectedFloorplanOptions.get(
                  userContext.selectedHouseLand.id
                ) || []
              }
              onClickShowLightbox={onClickShowLightbox}
              onClickOption={(floorplanOption) =>
                onClickFloorplanOption(floorplanOption as FloorplanOption)
              }
              floorplanNumber={0}
              totalFloorplans={0}
            />
          </div>

          {userContext.selectedHouseLand.inclusions_copy && (
            <div className={"inclusions-container container house-land-detail"}>
              <div className={"inner house-land-content"}>
                <h2>Inclusions</h2>
                <div className={"inclusions"}>
                  <div
                    className={"copy"}
                    dangerouslySetInnerHTML={{
                      __html: userContext.selectedHouseLand.inclusions_copy,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={"container"}>
            <h2>Location</h2>
          </div>
          <div className={"map-container container"}>
            <GoogleMap
              id={userContext.selectedHouseLand.id}
              latitude={userContext.selectedHouseLand.latitude}
              longitude={userContext.selectedHouseLand.longitude}
            />
          </div>
          <div className={"directions-container container"}>
            <p className={"h3"}>
              {userContext.selectedHouseLand.address}{" "}
              {userContext.selectedHouseLand.suburb}{" "}
              {userContext.selectedHouseLand.state}{" "}
              {userContext.selectedHouseLand.postcode}
            </p>

            {!userContext.isKioskMode && (
              <MoreDetailsBtn
                onClick={onClickGetDirections}
                title={"Get Directions"}
              />
            )}
          </div>
        </>
      )}

      {isLightboxShown && userContext.selectedHouseLand && (
        <FloorplanLightbox
          home={userContext.selectedHouseLand}
          svg={lightboxSvg}
          selectedOptions={
            userContext.selectedFloorplanOptions.get(
              userContext.selectedHouseLand.id
            ) || []
          }
          onClose={() => setIsLightboxShown(false)}
          isFloorplanFlipped={userContext.isFloorplanFlipped.get(userContext.selectedHouseLand.id) ||false}
        />
      )}
    </div>
  );
});

export default HouseLandDetailsScreen;
